import type { User as FirebaseUser } from 'firebase/auth';
import { gmailAddressOrNull } from 'shared/utils/gmail';

/**
 * PURE helpers for new-account creation — no firebase/nanoid imports, so the
 * exact-shape test stays hermetic. Ported field-for-field from aligned's
 * UserService.createNewUserAccount + user_account_model.g.dart `toJson`.
 *
 * ⚠️ Reviewer focus: `buildNewUserAccountDoc` is the exact initial document
 * written to the SHARED `user_accounts` collection. See newUserAccountDoc.test.
 */

// Hardcoded default pools every new expert joins (aligned user_service.dart).
export const DEFAULT_EXPERT_POOL_IDS = [
  'YCocXJUY2ongdsdo5jen', // All Experts Pool
  'A13cAt6shCeNe3xSXvjx', // Languages Pool
  'nb4CvNgEJSKTeSHwwIO2', // English Language Pool
];

// $20/hr in micro-USD (MicroUSD(20 * 1000000).toInt()).
export const DEFAULT_HOURLY_WAGE_BID_MICRO_USD = 20 * 1000000;

export interface NewUserAccountParams {
  id: string;
  firebaseId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePictureUrl: string | null;
  /** ISO-8601 UTC string (UTCDateTimeConverter.toJson === Date#toISOString). */
  createdAt: string;
}

/**
 * The exact initial `user_accounts/{id}` document. Field order and null
 * inclusion mirror aligned's generated `_$UserAccountToJson`.
 */
export function buildNewUserAccountDoc(params: NewUserAccountParams): Record<string, unknown> {
  return {
    id: params.id,
    firebaseId: params.firebaseId,
    email: params.email,
    firstNamePreferred: null,
    firstName: params.firstName,
    lastName: params.lastName,
    isVerifiedEmail: true,
    isVerifiedEmailSecondary: false,
    isVerifiedLinkedIn: false,
    isSubscribedEmail: true,
    isSubscribedPush: false,
    hasCompletedAccountInfo: false,
    hasCompletedIdentityVerification: false,
    hasCompletedOnboardingChecklist: false,
    hasAutoExpertPoolAssignments: false,
    createdAt: params.createdAt,
    lastSignInDate: null,
    lastSignupEmailReminderSentAt: null,
    lastSignupEmailReminderStage: null,
    signupEmailRemindersDisabled: false,
    profilePictureUrl: params.profilePictureUrl,
    profilePictureBase64: null,
    lastAppType: null,
    assignedExpertPoolIds: [...DEFAULT_EXPERT_POOL_IDS],
    onboardedTaskTypes: [],
    taskInstructionsHidden: [],
    paymentHoldHistory: [],
    suspensionHistory: [],
    unassignedJobs: [],
    raterOnboardingComplete: false,
    managerOnboardingComplete: false,
    hasSeenMessageBoardAssistantNotification: false,
    hasSeenMessageBoardIosAppNotification: false,
    isAdmin: false,
    isContractorAdmin: false,
    isAutoRater: false,
    canSeeMessageBoard: false,
    sentUpdateLinkedinRequest: false,
    isOperationsManager: false,
    messageBoardUserName: null,
    currentHourlyWageBid: DEFAULT_HOURLY_WAGE_BID_MICRO_USD,
    currentCustomerId: null,
    currentPendingExpertEarningsId: null,
    expertTierEducation: 'none',
    expertTierProfessional: 'none',
    expertTierAligned: 'none',
    expertTierJustification: null,
    summaryProfessionalExperience: null,
    summaryEducation: null,
    status: 'active',
    accountDisqualificationReason: null,
    reviewPriority: 'unreviewed',
    reviewEvidenceRequested: false,
    suspendedOn: null,
    suspendedUntil: null,
    suspendedBy: null,
    starRating: null,
    referralOpportunityId: null,
    publicationLinks: [],
    publicationCount: null,
    publicationHindex: null,
  };
}

export interface NewPrivateInfoParams {
  userAccountId: string;
  firebaseId: string;
  /** Sign-in email; decides whether `googleDriveEmail` is seeded. */
  email: string;
  unsubscribeToken: string;
}

/**
 * The initial `user_accounts/{id}/private/info` document. Mirrors aligned's
 * `{userAccountId, firebaseId, unsubscribeToken}` and adds beta's
 * `googleDriveEmail` only when the sign-in address is a Gmail account — a
 * Google account we know works for Drive sharing, so the expert is never asked
 * for one. A non-Gmail signup writes exactly the aligned shape.
 */
export function buildNewPrivateInfoDoc(params: NewPrivateInfoParams): Record<string, unknown> {
  const googleDriveEmail = gmailAddressOrNull(params.email);
  return {
    userAccountId: params.userAccountId,
    firebaseId: params.firebaseId,
    unsubscribeToken: params.unsubscribeToken,
    ...(googleDriveEmail ? { googleDriveEmail } : {}),
  };
}

export function normalizeEmail(email: string): string | null {
  const trimmed = email.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

export interface ProviderProfile {
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePictureUrl: string | null;
}

/** Provider-specific profile (Google first, matching aligned), falling back to
 *  the Firebase user's own fields. */
export function profileFromUser(user: FirebaseUser): ProviderProfile {
  const google = user.providerData.find((p) => p?.providerId === 'google.com');
  const displayName = google?.displayName ?? user.displayName ?? '';
  const parts = displayName.trim().split(' ').filter(Boolean);
  return {
    email: google?.email ?? user.email ?? '',
    firstName: parts.length ? parts[0] : null,
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : null,
    profilePictureUrl: google?.photoURL ?? user.photoURL ?? null,
  };
}
